import type React from "react"
import { useEffect, useState } from "react"
import { Box, Card, CardContent, Typography, Grid, CircularProgress, Alert } from "@mui/material"
import { requestService } from "../../../services/CrmRequestService"
import type { RequestCountersDto } from "../../../types/crm/types/request"
import { useAuth } from "../../../context/AuthContext"

interface RequestCountersProps {
  userProfileId: number
  userRole: string
  onCountersUpdate?: (total: number, pending: number, approved: number, rejected: number) => void
}

const RequestCounters: React.FC<RequestCountersProps> = ({ userProfileId, userRole, onCountersUpdate }) => {
  const [counters, setCounters] = useState<RequestCountersDto | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const { userToken } = useAuth();

  useEffect(() => {
    const profiles = userToken?.user.profiles || [];

    const fetchCounters = async () => {
      try {
        setLoading(true)

        // Verificar que los parámetros sean correctos
        if (!userProfileId || !userRole) {
          console.error("Parámetros inválidos:", { userProfileId, userRole })
          throw new Error("ID de perfil o rol no válidos")
        }

        // Determinar qué ID usar según el rol
        let idToUse = userProfileId
        const roleToUse = userRole

        // Si el rol es Broker, necesitamos usar el ID del realtor
        if (userRole === "Broker") {
          // Buscar el perfil activo
          const activeProfile = profiles?.find((profile) => profile.id === userProfileId)

          if (activeProfile && activeProfile.realtor) {
            // Usar el ID del realtor para los contadores
            idToUse = activeProfile.realtor.id
          } else {
            console.error("No se encontró información del realtor para el perfil activo")
            throw new Error("No se encontró información del realtor para el perfil activo")
          }
        }

        // Usar el ID apropiado según el rol
        const data = await requestService.getRequestCounters(idToUse, roleToUse)

        // Verificar que los datos tengan la estructura esperada
        if (data && typeof data.totalRequests === "number") {
          setCounters(data)

          // Notificar al componente padre sobre los contadores
          if (onCountersUpdate) {
            onCountersUpdate(data.totalRequests, data.inProgress, data.approved, data.rejected)
          }
        } else {
          console.error("Estructura de datos inesperada:", data)
          throw new Error("Formato de respuesta inesperado")
        }

        setError(null)
      } catch (err: any) {
        console.error("Error en fetchCounters:", err)
        setError(err.message || "Error al cargar los contadores")

        // En caso de error, establecer contadores en cero
        setCounters({
          totalRequests: 0,
          inProgress: 0,
          rejected: 0,
          approved: 0,
        })

        // Notificar al componente padre sobre los contadores en cero
        if (onCountersUpdate) {
          onCountersUpdate(0, 0, 0, 0)
        }
      } finally {
        setLoading(false)
      }
    }

    let isMounted = true

    if (userProfileId && userRole) {
      //Temporizador para evitar múltiples llamadas rápidas
      const timer = setTimeout(() => {
        if (isMounted) {
          fetchCounters()
        }
      }, 300)

      // Limpiar el temporizador cuando el componente se desmonte o las dependencias cambien
      return () => {
        isMounted = false
        clearTimeout(timer)
      }
    }

    // Función de limpieza para cuando el componente se desmonte
    return () => {
      isMounted = false
    }
  }, [userProfileId, userRole, onCountersUpdate, userToken?.user.profiles]) // Added onCountersUpdate as a dependency

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <CircularProgress size={30} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
      <Alert severity="info" sx={{ width: "100%" }}>
        <Typography sx={{ fontSize: { xs: "0.9rem", sm: "1rem" }, fontWeight: 500 }}>
          No se encontraron resultados.
        </Typography>
        <Typography
          variant="caption"
          display="block"
          sx={{
            mt: 1,
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            color: "text.secondary",
          }}
        >
        </Typography>
      </Alert>
    </Box>

    )
  }

  if (!counters) {
    return null
  }

  // Actualizar los colores según la tabla proporcionada
  const counterItems = [
    { label: "Total", value: counters.totalRequests, color: "#2C6E6F" },
    { label: "Pendientes", value: counters.inProgress, color: "#FFA500" }, // Pendiente - ID 4
    { label: "Aprobadas", value: counters.approved, color: "#00FF00" }, // Aprobada - ID 6
    { label: "Rechazadas", value: counters.rejected, color: "#FF0000" }, // Rechazada - ID 5
  ]

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
        Resumen de Solicitudes
      </Typography>
      <Grid container spacing={{ xs: 1, sm: 2 }}>
        {counterItems.map((item) => (
          <Grid item xs={6} sm={3} key={item.label}>
            <Card
              elevation={2}
              sx={{
                borderTop: `4px solid ${item.color}`,
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                },
              }}
            >
              <CardContent
                sx={{
                  textAlign: "center",
                  py: { xs: 1, sm: 2 },
                  px: { xs: 1, sm: 2 },
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color={item.color}
                  sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" } }}
                >
                  {item.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
                  {item.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default RequestCounters